import TextField from '@app/src/components/Ui/TextField'
import { QuestionType } from '@app/src/types/reporting'
import {
  AnswerClassificationValue,
  AssessmentRuleType,
  AssessmentType,
  QuestionAnswerClassificationRule,
  QuestionAnswerRank,
  QuestionAnswerRankScoringType,
} from '@app/src/types/resourceExplorer'
import { InfoOutlined } from '@mui/icons-material'
import { Alert, Box, Divider, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { DEFAULT_ASSESSMENT_POINTS } from '../../../components/Configurations/SustainabilityLibraryPages/Assessments/utils/constants'
import Select from '../../../components/Form/Select/ControlledSelect'
import AdvancedClassification from './AdvancedClassification'

type QuestionAnswerClassificationRuleSaveModel = QuestionAnswerClassificationRule & {
  disabled?: boolean
}

type EditQuestionAnswerClassificationProps = {
  questionTypeId: QuestionType
  assessmentType?: AssessmentType
}

const EditQuestionAnswerClassification: React.FC<EditQuestionAnswerClassificationProps> = ({
  questionTypeId,
  assessmentType = AssessmentType.StandardAssessment,
}) => {
  const { formatMessage } = useIntl()
  const {
    control,
    watch,
    setValue,
    register,
    formState: { errors },
  } = useFormContext()

  const watchImpactValueType = watch('impactValueType')

  const watchQuestionAnswerClassificationRules: Array<QuestionAnswerClassificationRuleSaveModel | undefined> = watch(
    'questionAnswerClassificationRules',
  )

  const presentValues = useMemo(
    () =>
      Object.values(watchQuestionAnswerClassificationRules ?? {})
        .flat()
        .filter(Boolean)
        .filter(item => item?.rank !== 'NotSet')
        .map(item => item?.value),
    [watchQuestionAnswerClassificationRules],
  )

  useEffect(() => {
    if (assessmentType === AssessmentType.StandardAssessment) {
      setValue('impactValueType', QuestionAnswerRankScoringType.Default)
    }
  }, [assessmentType])

  useEffect(() => {
    if (watchImpactValueType === QuestionAnswerRankScoringType.Default) {
      setValue('points.positive', DEFAULT_ASSESSMENT_POINTS.POSITIVE)
      setValue('points.negative', DEFAULT_ASSESSMENT_POINTS.NEGATIVE)
      setValue('points.negativeAdverse', DEFAULT_ASSESSMENT_POINTS.NEGATIVE_ADVERSE)
      setValue('points.neutral', DEFAULT_ASSESSMENT_POINTS.NEUTRAL)
    }
  }, [watchImpactValueType, setValue])

  return (
    <Stack spacing={3}>
      {assessmentType === AssessmentType.Flexible && (
        <Box width="40%">
          <Select
            name="impactValueType"
            control={control}
            size="small"
            defaultValue={QuestionAnswerRankScoringType.Default}
            fieldLabel={formatMessage({ id: 'assessments.impactValues' })}
            options={[
              {
                label: formatMessage({ id: 'general.default' }),
                value: QuestionAnswerRankScoringType.Default,
              },
              {
                label: formatMessage({ id: 'configurations.custom' }),
                value: QuestionAnswerRankScoringType.Custom,
              },
            ]}
          />
        </Box>
      )}
      {watchImpactValueType === QuestionAnswerRankScoringType.Custom && (
        <Stack direction="row" spacing={2}>
          <TextField
            size="small"
            type="number"
            name="points.positive"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.POSITIVE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.Positive' })}
            error={!!errors.points?.positive}
            helperText={errors.points?.positive?.message}
          />
          <TextField
            size="small"
            type="number"
            name="points.negative"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.NEGATIVE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.Negative' })}
            error={!!errors.points?.negative}
            helperText={errors.points?.negative?.message}
          />
          <TextField
            size="small"
            type="number"
            name="points.negativeAdverse"
            defaultValue={DEFAULT_ASSESSMENT_POINTS.NEGATIVE_ADVERSE}
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            label={formatMessage({ id: 'templateBuilder.answerClassifications.classificationsRanks.NegativeAdverse' })}
            error={!!errors.points?.negativeAdverse}
            helperText={errors.points?.negativeAdverse?.message}
          />
        </Stack>
      )}
      {assessmentType === AssessmentType.Flexible && (
        <>
          <Divider />
          <Alert severity="warning">
            <Typography>{formatMessage({ id: 'assessments.impactValuesWarning' })}</Typography>
          </Alert>
        </>
      )}
      {assessmentType === AssessmentType.StandardAssessment && (
        <Box>
          <Typography variant="subtitle1" gutterBottom>
            {formatMessage({ id: 'templateBuilder.answerClassifications.title' })}
          </Typography>
          <Box display="flex" alignItems="center">
            <InfoOutlined fontSize="small" color="action" />
            <Typography variant="caption" color="textSecondary" ml={1}>
              {formatMessage({ id: 'templateBuilder.answerClassifications.subtitle' })}
            </Typography>
          </Box>
        </Box>
      )}
      {Object.values(QuestionAnswerRank)
        .filter(item => item !== QuestionAnswerRank.NotSet)
        .map(item => (
          <Box key={item}>
            <Select<QuestionAnswerClassificationRuleSaveModel>
              name={`questionAnswerClassificationRules[${item}]`}
              control={control}
              fieldLabel={formatMessage({
                id: `templateBuilder.answerClassifications.classificationsRanks.${item}`,
              })}
              size="small"
              multiple
              options={[
                {
                  label: formatMessage({
                    id: `templateBuilder.answerClassifications.classificationsValues.${AnswerClassificationValue.Answered}`,
                  }),
                  value: {
                    rank: item,
                    ruleType: AssessmentRuleType.AnswerStatus,
                    value: AnswerClassificationValue.Answered,
                  },
                  disabled: presentValues.includes(AnswerClassificationValue.Answered),
                },
                {
                  label: formatMessage({
                    id: `templateBuilder.answerClassifications.classificationsValues.${AnswerClassificationValue.NotApplicable}`,
                  }),
                  value: {
                    rank: item,
                    ruleType: AssessmentRuleType.AnswerStatus,
                    value: AnswerClassificationValue.NotApplicable,
                  },
                  disabled: presentValues.includes(AnswerClassificationValue.NotApplicable),
                },
                {
                  label: formatMessage({
                    id: `templateBuilder.answerClassifications.classificationsValues.${AnswerClassificationValue.NotAvailable}`,
                  }),
                  value: {
                    rank: item,
                    ruleType: AssessmentRuleType.AnswerStatus,
                    value: AnswerClassificationValue.NotAvailable,
                  },
                  disabled: presentValues.includes(AnswerClassificationValue.NotAvailable),
                },
                {
                  label: formatMessage({
                    id: `templateBuilder.answerClassifications.classificationsValues.${AnswerClassificationValue.Other}`,
                  }),
                  value: {
                    rank: item,
                    ruleType: AssessmentRuleType.AnswerStatus,
                    value: AnswerClassificationValue.Other,
                  },
                  disabled: presentValues.includes(AnswerClassificationValue.Other),
                },
              ]}
              findSelectedValue={(value, option) => value?.value === option?.value?.value}
              onInputChange={(_e, value) => {
                if (!value) setValue(`questionAnswerClassificationRules[${item}]`, undefined)
              }}
            />
          </Box>
        ))}

      <AdvancedClassification questionTypeId={questionTypeId} />
    </Stack>
  )
}

export default EditQuestionAnswerClassification
